import type { Match } from '@uefadigital/panenka';
import { PkBadge, PkIdentifier, PkJersey } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { LineUpsField } from 'components/LineUpsField';
import type { LineUps as LineUp, LineUpPlayer, LineUpTeam, SquadPlayer } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';
import { translateEntity } from 'utils/translateEntity';

const cssPropertiesPrimary: CSSProperties = {
  fontWeight: 'var(--pk-font-weight-m)',
  fontFamily: 'var(--pk-font-base-regular)',
  lineHeight: 'var(--pk-line-height--sm)',
};

const GOALKEEPER = 'GOALKEEPER';

interface FieldSizeData {
  desktop: number;
  mobile: number;
  desktopJerseySize: number;
  mobileJerseySize: number;
}

interface LineUpsData {
  match: Match;
  isDesktop: boolean;
  fieldSize: FieldSizeData;
  competition?: ConfigCompetition;
  noPosition?: boolean;
  lineUps?: LineUp;
  isOverview?: boolean;
}

export const LineUps = ({ match, isDesktop, fieldSize, competition, noPosition, lineUps, isOverview }: LineUpsData) => {
  const { t } = useTranslation();
  const titleStyle = clsx(
    'pk-font--comp-bold pk-text--text-01 pk-font-style--normal pk-line-height--sm pk-font-size--xl pk-font-size-md--xl2',
    isDesktop && 'pr-font-size--16',
  );

  const displayPlayerName = (player: LineUpPlayer | SquadPlayer) => {
    if (translateEntity(player.player)?.shortName) {
      return translateEntity(player.player).shortName;
    }

    if ('jerseyNumber' in player) {
      return `${t('player')} ${player.jerseyNumber}`;
    }
    return t('player');
  };

  const displayBenchPlayers = (team: LineUpTeam) => {
    return team.bench.map((player) => {
      return (
        <PkIdentifier className="pk-py--xs2" key={player.player.id}>
          <span slot="prefix" className="pk-mr--s">
            <PkJersey shirtColor={team.shirtColor} number={player.jerseyNumber} size={25} />
          </span>
          <span
            slot="primary"
            style={cssPropertiesPrimary}
            className={`${
              isDesktop ? 'pk-font-size--l pk-line-height--md pr-font-size--12' : 'pk-font-size--m pk-line-height--sm'
            }`}
          >
            {displayPlayerName(player)} {player.type === GOALKEEPER ? `(${t('gk')})` : null}
          </span>
        </PkIdentifier>
      );
    });
  };

  return (
    <div>
      {isDesktop && !isOverview && (
        <div className={clsx('pr-pb--24', noPosition ? 'pk-mb--l' : 'pk-mb--xl2')}>
          <div className={titleStyle}>{t('line_ups')}</div>
        </div>
      )}
      {!isDesktop && noPosition && (
        <div className={clsx('pr-pb--24', isOverview ? 'pk-mb--m' : 'pk-mb--l')}>
          <div className={titleStyle}>{t('line_ups')}</div>
        </div>
      )}
      {noPosition && match.awayTeam && match.homeTeam && (
        <div className="pk-d--flex pk-justify-content-sm--between pk-mt-md--m pr-pt--0">
          <div className="flex-1 pk-ml--auto pk-ml-md--0">
            <PkIdentifier className="pk-pb--xs2">
              <span slot="prefix">
                <PkBadge
                  alt={match.homeTeam.internationalName ?? ''}
                  size={isDesktop ? 56 : 24}
                  fallbackImage="club-generic-badge"
                  src={match.homeTeam.logoUrl}
                  className="pr-badge-size--30"
                />
              </span>
              {translateEntity(match.homeTeam)?.displayName && (
                <span
                  slot="primary"
                  className={`${
                    isDesktop
                      ? `${isOverview ? 'pk-font-size--l' : 'pk-font-size--xl'} pk-line-height--md pr-font-size--14`
                      : 'pk-font-size--m pk-line-height--sm'
                  } `}
                >
                  {translateEntity(match.homeTeam).displayName}
                </span>
              )}
            </PkIdentifier>
          </div>
          <div className="flex-1">
            <PkIdentifier className="pk-pb--xs2">
              <span slot="prefix">
                <PkBadge
                  alt={match.awayTeam?.internationalName ?? ''}
                  size={isDesktop ? 56 : 24}
                  fallbackImage="club-generic-badge"
                  src={match.awayTeam.logoUrl}
                  className="pr-badge-size--30"
                />
              </span>
              <span
                slot="primary"
                className={`${
                  isDesktop
                    ? `${isOverview ? 'pk-font-size--l' : 'pk-font-size--xl'} pk-line-height--md pr-font-size--14`
                    : 'pk-font-size--m pk-line-height--sm'
                } `}
              >
                {translateEntity(match?.awayTeam)?.displayName}
              </span>
            </PkIdentifier>
          </div>
        </div>
      )}
      {noPosition ? (
        lineUps &&
        lineUps.homeTeam && (
          <div>
            <div className="pk-d--flex pk-justify-content-sm--between pk-mt-md--0 pk-mb-md--xs">
              <div className="flex-1">{displayBenchPlayers(lineUps.homeTeam)}</div>
              <div className="flex-1">{displayBenchPlayers(lineUps.awayTeam)}</div>
            </div>
          </div>
        )
      ) : (
        <div className="pk-mt-md--m">
          <LineUpsField
            match={match}
            previousMatch={false}
            isDesktop={isDesktop}
            fieldSize={fieldSize}
            competition={competition}
          />
        </div>
      )}
    </div>
  );
};
